import React, { useEffect } from 'react';
import { Box, Typography, Grid, Paper, Table, TableHead, TableBody, TableRow, TableCell,  TableContainer, } from '@mui/material';
import { getSemenFreezingReport, SemenFreezing } from './SemenFreezingReport.helper';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
import { useLoader } from '../../loaderProvider/loader';
import { useTranslation } from 'react-i18next';


interface SemenFreezingReportProps {
  cycle: string;
}

const SemenFreezingReport: React.FC<SemenFreezingReportProps> = ({ cycle }) => {
  const [semenFreezingDetails, setSemenFreezingDetails] = React.useState<SemenFreezing[]>([]);
  const { showLoader, hideLoader } = useLoader();
  const { t } = useTranslation('cycleOverview_config');

  useEffect(()=>{
    try {
      showLoader();
      getSemenFreezingReport(cycle).then((res:SemenFreezing[])=>{
        setSemenFreezingDetails(res);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching semen freezing report:', error);
    } finally {
      hideLoader();
    }
  },[cycle, showLoader, hideLoader]);

  function formatTo12Hour(timeStr: string): string {
    if (!timeStr) {
      return '-';
    }
    const [hourStr, minute] = timeStr.split(':');
    let hour = parseInt(hourStr, 10);

    const period = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // Convert "0" to "12" for 12 AM

    return `${hour}:${minute} ${period}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getDisplayValue = (value: any) => {
    return value !== null && value !== undefined && value !== '' ? value : '-';
  };

  return (
    <Box >
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Box 1: Details */}
        <Box
          p={2}
          width="100%"
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            {t('Details', 'Details')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('SampleNo', 'Sample No')}
              </Typography>
              <Typography fontWeight={600}>{semenFreezingDetails[0]?.prefreezing_save ? semenFreezingDetails[0]?.sample_number : '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('Date', 'Date')}
              </Typography>
              <Typography fontWeight={600}>{semenFreezingDetails[0]?.date || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('Time', 'Time')}
              </Typography>
              <Typography fontWeight={600}>{formatTo12Hour(semenFreezingDetails[0]?.time)}</Typography>
            </Grid>
          </Grid>
        </Box>
        <hr/>

        <Box
          p={2}
          width="100%"
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            {t('OtherDetails', 'Other Details')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('TypeOfSperm', 'Type of Sperm')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(convertUnderscoreToTitle(semenFreezingDetails[0]?.cryo[0]?.type_of_sperm))}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('CollectionDateTime', 'Collection Date & Time')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(  semenFreezingDetails[0]?.collection_date)} {semenFreezingDetails[0]?.collection_time ? `${t('at', 'at')} ${formatTo12Hour(semenFreezingDetails[0]?.collection_time)}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('ReceivingDateTime', 'Receiving Date & Time')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(  semenFreezingDetails[0]?.receiving_date)} {semenFreezingDetails[0]?.receiving_time ? `${t('at', 'at')} ${formatTo12Hour(semenFreezingDetails[0]?.receiving_time)}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('SurgicalExtractionType', 'Surgical Extraction Type')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.surgical_extraction_type || '-')}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('Abstinence', 'Abstinence')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.abstinence_period)}{semenFreezingDetails[0]?.abstinence_period ? ` ${t('days', 'days')}` : ''}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('Viscosity', 'Viscosity')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.viscosity_label)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('Volume', 'Volume')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.volume_ml)}{semenFreezingDetails[0]?.volume_ml ? ` ${t('ml', 'ml')}` : ''}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('SpermConcentration', 'Sperm Concentration')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.sperm_concentration_million_per_ml)}{semenFreezingDetails[0]?.sperm_concentration_million_per_ml ? ` ${t('mPerMl', 'm/mL')}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('Total_Sperm_Count', 'Total Sperm Count')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.cryo[0]?.total_sperm_count)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
                {t('Collection_Details', 'Collection Details')}
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.semen_collection_method_label)}
              </Typography>
            </Grid>
            
          </Grid>
        </Box>
      </Box>
      <hr/>

      <Box mt={4}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          {t('CryostorageDetails', 'Cryostorage Details')}
        </Typography>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #ddd' }}>
          <Table sx={{ borderCollapse: 'collapse' }}>
            <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
              <TableRow>
                {[
                  t('CryoNo', 'Cryo No.'),
                  t('Tank', 'Tank'),
                  t('Canister', 'Canister'),
                  t('Cane', 'Cane'),
                  t('Vials', 'Vials'),
                  t('Volume', 'Volume'),
                  t('ExpiryDate', 'Expiry Date'),
                  t('Remark', 'Remark'),
                ].map((header, idx) => (
                  <TableCell key={idx} sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {semenFreezingDetails[0]?.cryo.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 600 }}>{row.id}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.parents[0].name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.parents[1].name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.parents[2].name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.semen_volume}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.expiry_date}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.remark}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        gap={10}
        mt={2}
        p={2}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography color="text.secondary" fontSize={14}>
              {t('NoOfVials', 'No. of Vials')}
            </Typography>
            <Typography fontWeight={600}>{semenFreezingDetails[0]?.cryo.length > 0 ? semenFreezingDetails[0]?.cryo.length : '-'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography color="text.secondary" fontSize={14}>
              {t('DoneBy', 'Done By')}
            </Typography>
            <Typography fontWeight={600}>{getDisplayValue(semenFreezingDetails[0]?.andrologist_name)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography color="text.secondary" fontSize={14}>
              {t('WitnessedBy', 'Witnessed By')}
            </Typography>
            <Typography fontWeight={600}>{getDisplayValue(semenFreezingDetails[0]?.witnessed_by_name)}</Typography>
          </Grid>
        </Grid>
      </Box>


    </Box>
  );
};

export default SemenFreezingReport;
